/* ==========================================================================
   BRAND LOUNGE
   Every button, section, and card already exists in the DOM (rendered by
   Liquid from the metaobject data). This file does exactly two things:
   respond to clicks, and toggle visibility/order on existing elements.
   It never builds HTML and never reads a JSON payload.
   ========================================================================== */
(function () {
  'use strict';

  // Reads a card's "Category:Sub" pairs (joined with ‖ in the data attribute)
  // and checks whether one of them matches the active category + subcategory.
  function hasSub(card, cat, sub) {
    var raw = card.getAttribute('data-subcats');
    if (!raw) return false;
    return raw.split('‖').some(function (entry) {
      var idx = entry.indexOf(':');
      if (idx === -1) return false;
      var entryCat = entry.slice(0, idx).trim();
      var entrySub = entry.slice(idx + 1).trim();
      return entryCat === cat && entrySub === sub;
    });
  }

  function cardCategories(card) {
    var raw = card.getAttribute('data-categories');
    return raw ? raw.split('‖') : [];
  }

  function initBrandLounge(root) {
    if (root.__blInitialized) return;
    root.__blInitialized = true;
    initDesktop(root);
    initMobile(root);
  }

  /* ========================================================================
     DESKTOP
     ======================================================================== */
  function initDesktop(root) {
    var scope = root.querySelector('.bl-desktop');
    if (!scope) return;

    var azBtns = scope.querySelectorAll('[data-bl-az-btn]');
    var catBtns = scope.querySelectorAll('[data-bl-cat-btn]');
    var subWrap = scope.querySelector('[data-bl-sub-wrap]');
    var subGroups = scope.querySelectorAll('[data-bl-sub-group]');
    var cards = scope.querySelectorAll('[data-bl-card]');
    var letterSections = scope.querySelectorAll('[data-bl-letter-section]');
    var emptyEl = scope.querySelector('[data-bl-empty]');
    var navCount = scope.querySelector('[data-bl-nav-count]');
    var resetBtn = scope.querySelector('[data-bl-reset]');

    var S = { cat: 'All', sub: 'All' };

    function applyFilters() {
      var visibleCount = 0;
      cards.forEach(function (card) {
        var cats = cardCategories(card);
        var sale = card.getAttribute('data-sale') === 'true';
        var cm = S.cat === 'All' || (S.cat === 'Sale' ? sale : cats.indexOf(S.cat) !== -1);
        var sm = S.sub === 'All' || hasSub(card, S.cat, S.sub);
        var visible = cm && sm;
        card.style.display = visible ? '' : 'none';
        if (visible) visibleCount++;
      });

      letterSections.forEach(function (sec) {
        var sectionCards = sec.querySelectorAll('[data-bl-card]');
        var hasVisible = Array.prototype.some.call(sectionCards, function (c) { return c.style.display !== 'none'; });
        sec.style.display = hasVisible ? '' : 'none';
      });

      if (emptyEl) emptyEl.classList.toggle('show', visibleCount === 0);
      if (navCount) navCount.textContent = visibleCount + ' brand' + (visibleCount !== 1 ? 's' : '');
    }

    function setActive(list, matchFn) {
      list.forEach(function (el) {
        var active = matchFn(el);
        el.classList.toggle('active', active);
        el.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    function showSubGroup(cat) {
      var found = null;
      subGroups.forEach(function (g) {
        var match = g.getAttribute('data-cat') === cat;
        g.classList.toggle('show', match);
        if (match) found = g;
      });
      if (subWrap) subWrap.classList.toggle('show', !!found);
      if (found) {
        found.querySelectorAll('[data-bl-sub-btn]').forEach(function (b) {
          var active = b.getAttribute('data-sub') === 'All';
          b.classList.toggle('active', active);
          b.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
      }
    }

    azBtns.forEach(function (btn) {
      if (btn.getAttribute('aria-disabled') === 'true') return;
      btn.addEventListener('click', function () {
        var l = btn.getAttribute('data-letter');
        var target = scope.querySelector('[data-bl-letter-section][data-letter="' + l + '"]');
        // If the current category/subcategory filter has hidden this letter's
        // section entirely, there's nothing to scroll to.
        if (!target || target.style.display === 'none') return;
        azBtns.forEach(function (b) { b.classList.toggle('active', b === btn); });
        var navEl = scope.querySelector('.bl-nav');
        var filtersEl = scope.querySelector('.bl-filters');
        var offset = (navEl ? navEl.offsetHeight : 0) + (filtersEl ? filtersEl.offsetHeight : 0) + 16;
        var top = target.getBoundingClientRect().top + window.pageYOffset - offset - 160;
        window.scrollTo({ top: top, behavior: 'smooth' });
      });
    });

    catBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var cat = btn.getAttribute('data-cat');
        S.cat = cat;
        S.sub = 'All';
        setActive(catBtns, function (b) { return b === btn; });
        showSubGroup(cat);
        applyFilters();
      });
    });

    subGroups.forEach(function (group) {
      group.querySelectorAll('[data-bl-sub-btn]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          S.sub = btn.getAttribute('data-sub');
          group.querySelectorAll('[data-bl-sub-btn]').forEach(function (b) {
            var active = b === btn;
            b.classList.toggle('active', active);
            b.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
          applyFilters();
        });
      });
    });

    if (resetBtn) {
      resetBtn.addEventListener('click', function () {
        S = { cat: 'All', sub: 'All' };
        setActive(catBtns, function (b) { return b.getAttribute('data-cat') === 'All'; });
        subGroups.forEach(function (g) { g.classList.remove('show'); });
        if (subWrap) subWrap.classList.remove('show');
        applyFilters();
      });
    }

    applyFilters();
  }

  /* ========================================================================
     MOBILE
     ======================================================================== */
  function initMobile(root) {
    var scope = root.querySelector('.bl-mobile');
    if (!scope) return;

    var chips = scope.querySelectorAll('[data-bl-chip]');
    var sortBtns = scope.querySelectorAll('[data-bl-sort-btn]');
    var cards = scope.querySelectorAll('[data-bl-card]');
    var emptyEl = scope.querySelector('[data-bl-m-empty]');
    var hdrCount = scope.querySelector('[data-bl-m-count]');
    var searchInput = scope.querySelector('[data-bl-search-input]');
    var searchClear = scope.querySelector('[data-bl-search-clear]');
    var fab = scope.querySelector('[data-bl-fab]');
    var fabDot = scope.querySelector('[data-bl-fab-dot]');
    var backdrop = scope.querySelector('[data-bl-backdrop]');
    var drawer = scope.querySelector('[data-bl-drawer]');
    var drawerClose = scope.querySelector('[data-bl-drawer-close]');
    var dCats = scope.querySelectorAll('[data-bl-d-cat]');
    var dSortPills = scope.querySelectorAll('[data-bl-d-sort-pill]');
    var dAZ = scope.querySelectorAll('[data-bl-d-az]');
    var dReset = scope.querySelector('[data-bl-d-reset]');
    var dApply = scope.querySelector('[data-bl-d-apply]');
    var sectionTitle = scope.querySelector('.bl-m-section-title');

    var S = { cat: 'All', search: '', sort: 'default', letter: '' };

    function applySort() {
      var arr = Array.prototype.slice.call(cards);
      if (S.sort === 'az') {
        arr.sort(function (a, b) { return a.getAttribute('data-name').localeCompare(b.getAttribute('data-name')); });
      } else if (S.sort === 'za') {
        arr.sort(function (a, b) { return b.getAttribute('data-name').localeCompare(a.getAttribute('data-name')); });
      } else {
        arr.sort(function (a, b) { return (b.getAttribute('data-featured') === 'true') - (a.getAttribute('data-featured') === 'true'); });
      }
      arr.forEach(function (card, i) { card.style.order = i; });
    }

    function applyFilters() {
      var visibleCount = 0;
      var q = S.search.toLowerCase();
      cards.forEach(function (card) {
        var cats = cardCategories(card);
        var sale = card.getAttribute('data-sale') === 'true';
        var cm = S.cat === 'All' || (S.cat === 'Sale' ? sale : cats.indexOf(S.cat) !== -1);
        var lm = !S.letter || card.getAttribute('data-letter') === S.letter;
        var qm = !q || (card.getAttribute('data-name-lower') || '').indexOf(q) !== -1;
        var visible = cm && lm && qm;
        card.style.display = visible ? '' : 'none';
        if (visible) visibleCount++;
      });
      applySort();
      if (emptyEl) emptyEl.classList.toggle('show', visibleCount === 0);
      if (hdrCount) hdrCount.textContent = visibleCount + ' brand' + (visibleCount !== 1 ? 's' : '');
    }

    function updateFabDot() {
      if (!fabDot) return;
      var count = (S.cat !== 'All' ? 1 : 0) + (S.letter ? 1 : 0);
      if (count > 0) {
        fabDot.textContent = String(count);
        fabDot.classList.add('on');
      } else {
        fabDot.textContent = '';
        fabDot.classList.remove('on');
      }
    }

    function updateSectionTitle(key) {
      if (!sectionTitle) return;
      if (key === 'All') { sectionTitle.textContent = 'All Brands'; return; }
      var chip = Array.prototype.find.call(chips, function (c) { return c.getAttribute('data-key') === key; });
      sectionTitle.textContent = chip ? chip.textContent.trim() : key;
    }

    function setFilter(key) {
      S.cat = key;
      chips.forEach(function (c) { c.classList.toggle('active', c.getAttribute('data-key') === key); });
      dCats.forEach(function (c) { c.classList.toggle('active', c.getAttribute('data-key') === key); });
      updateFabDot();
      updateSectionTitle(key);
      applyFilters();
    }

    function setSort(key) {
      S.sort = key;
      sortBtns.forEach(function (b) { b.classList.toggle('active', b.getAttribute('data-key') === key); });
      dSortPills.forEach(function (b) { b.classList.toggle('active', b.getAttribute('data-key') === key); });
      applyFilters();
    }

    function setLetter(l) {
      S.letter = (S.letter === l) ? '' : l;
      dAZ.forEach(function (el) { el.classList.toggle('active', el.getAttribute('data-letter') === S.letter); });
      updateFabDot();
      applyFilters();
    }

    function openDrawer() {
      if (backdrop) backdrop.classList.add('open');
      if (drawer) drawer.classList.add('open');
      document.body.style.overflow = 'hidden';
      var focusable = drawer && drawer.querySelector('button, [href], input, [role="button"]');
      if (focusable) focusable.focus();
    }
    function closeDrawer() {
      if (backdrop) backdrop.classList.remove('open');
      if (drawer) drawer.classList.remove('open');
      document.body.style.overflow = '';
    }

    chips.forEach(function (c) {
      c.addEventListener('click', function () { setFilter(c.getAttribute('data-key')); });
      c.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFilter(c.getAttribute('data-key')); } });
    });
    dCats.forEach(function (c) {
      c.addEventListener('click', function () { setFilter(c.getAttribute('data-key')); });
      c.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFilter(c.getAttribute('data-key')); } });
    });
    sortBtns.forEach(function (b) { b.addEventListener('click', function () { setSort(b.getAttribute('data-key')); }); });
    dSortPills.forEach(function (b) { b.addEventListener('click', function () { setSort(b.getAttribute('data-key')); }); });
    dAZ.forEach(function (el) {
      if (el.classList.contains('empty')) return;
      el.addEventListener('click', function () { setLetter(el.getAttribute('data-letter')); });
      el.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setLetter(el.getAttribute('data-letter')); } });
    });

    if (fab) fab.addEventListener('click', openDrawer);
    if (drawerClose) drawerClose.addEventListener('click', closeDrawer);
    if (backdrop) backdrop.addEventListener('click', closeDrawer);
    if (dApply) dApply.addEventListener('click', closeDrawer);

    if (searchInput) {
      searchInput.addEventListener('input', function () {
        S.search = searchInput.value;
        if (searchClear) searchClear.classList.toggle('show', !!S.search);
        applyFilters();
      });
    }
    if (searchClear) {
      searchClear.addEventListener('click', function () {
        S.search = '';
        if (searchInput) searchInput.value = '';
        searchClear.classList.remove('show');
        applyFilters();
      });
    }

    if (dReset) {
      dReset.addEventListener('click', function () {
        S = { cat: 'All', search: '', sort: 'default', letter: '' };
        if (searchInput) searchInput.value = '';
        if (searchClear) searchClear.classList.remove('show');
        chips.forEach(function (c) { c.classList.toggle('active', c.getAttribute('data-key') === 'All'); });
        dCats.forEach(function (c) { c.classList.toggle('active', c.getAttribute('data-key') === 'All'); });
        sortBtns.forEach(function (b) { b.classList.toggle('active', b.getAttribute('data-key') === 'default'); });
        dSortPills.forEach(function (b) { b.classList.toggle('active', b.getAttribute('data-key') === 'default'); });
        dAZ.forEach(function (el) { el.classList.remove('active'); });
        updateFabDot();
        updateSectionTitle('All');
        applyFilters();
      });
    }

    applyFilters();
  }

  function boot() {
    document.querySelectorAll('[data-brand-lounge]').forEach(initBrandLounge);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector && e.target.querySelector('[data-brand-lounge]');
    if (root) initBrandLounge(root);
  });
})();



// /* ==========================================================================
//    BRAND LOUNGE
//    Scoped per [data-brand-lounge] root so this is safe if the section is
//    ever added more than once on a page, and re-initializes correctly when
//    the theme editor hot-reloads the section.
//    ========================================================================== */
// (function () {
//   'use strict';

//   var ALPHABET = ['#'].concat('ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''));

//   // Buckets by the name's actual first character: letters get their own
//   // tile, anything else (digits, symbols) falls under "#".
//   function firstLetter(name) {
//     var c = (name || '').trim().charAt(0).toUpperCase();
//     return /[A-Z]/.test(c) ? c : '#';
//   }

//   // Builds the full filter-button list from the categories actually present
//   // in the data: "All" first, real categories in the middle, "On Sale" last
//   // — and only if at least one brand is actually flagged on_sale.
//   function buildCatList(categories, hasSale) {
//     var list = [{ label: 'All', key: 'All' }];
//     (categories || []).forEach(function (c) { list.push({ label: c, key: c }); });
//     if (hasSale) list.push({ label: 'On Sale', key: 'Sale' });
//     return list;
//   }

//   function initBrandLounge(root) {
//     if (root.__blInitialized) return;
//     root.__blInitialized = true;

//     var dataEl = root.querySelector('[data-bl-json]');
//     var BRANDS = [];
//     if (dataEl) {
//       try { BRANDS = JSON.parse(dataEl.textContent) || []; } catch (e) { BRANDS = []; }
//     }
//     BRANDS.forEach(function (b) { b.letter = firstLetter(b.name); });

//     var sectionId = root.getAttribute('data-bl-section-id');
//     var taxonomy = (window.BL_TAXONOMY && sectionId && window.BL_TAXONOMY[sectionId]) || {};
//     var categories = taxonomy.categories || [];
//     var subcatsMap = taxonomy.subcategories || {};
//     var catList = buildCatList(categories, taxonomy.hasSale);

//     initDesktop(root, BRANDS, catList, subcatsMap);
//     initMobile(root, BRANDS, catList, subcatsMap);
//   }

//   /* ========================================================================
//      DESKTOP
//      ======================================================================== */
//   function initDesktop(root, BRANDS, catList, subcatsMap) {
//     var scope = root.querySelector('.bl-desktop');
//     if (!scope) return;

//     var catRow = scope.querySelector('[data-bl-cat-row]');
//     var subRow = scope.querySelector('[data-bl-sub-row]');
//     var azRow = scope.querySelector('[data-bl-az-row]');
//     var contentEl = scope.querySelector('[data-bl-content]');
//     var emptyEl = scope.querySelector('[data-bl-empty]');
//     var navCount = scope.querySelector('[data-bl-nav-count]');

//     var S = { cat: 'All', sub: 'All', letter: null };

//     // A brand's raw subcategories are "Category: Subcategory" strings.
//     // Matches only when both the active category and the label agree.
//     function hasSub(b, cat, sub) {
//       return (b.subcategories || []).some(function (raw) {
//         var parts = String(raw).split(':');
//         if (parts.length < 2) return false;
//         return parts[0].trim() === cat && parts.slice(1).join(':').trim() === sub;
//       });
//     }

//     function filtered() {
//       return BRANDS.filter(function (b) {
//         var cm = S.cat === 'All' || (S.cat === 'Sale' ? b.sale === true : (b.categories || []).indexOf(S.cat) !== -1);
//         var sm = S.sub === 'All' || hasSub(b, S.cat, S.sub);
//         var lm = !S.letter || b.letter === S.letter;
//         return cm && sm && lm;
//       });
//     }

//     function buildCats() {
//       if (!catRow) return;
//       catList.forEach(function (c) {
//         var catKey = c.key;
//         var b = document.createElement('button');
//         b.type = 'button';
//         b.className = 'bl-cat-btn' + (catKey === 'All' ? ' active' : '') + (catKey === 'Sale' ? ' sale-btn' : '');
//         b.setAttribute('aria-pressed', catKey === 'All' ? 'true' : 'false');
//         b.dataset.cat = catKey;
//         b.textContent = c.label;
//         b.onclick = function () {
//           S.cat = catKey;
//           S.sub = 'All';
//           catRow.querySelectorAll('.bl-cat-btn').forEach(function (x) {
//             var active = x === b;
//             x.classList.toggle('active', active);
//             x.setAttribute('aria-pressed', active ? 'true' : 'false');
//           });
//           buildSub(catKey === 'Sale' ? null : catKey);
//           render();
//         };
//         catRow.appendChild(b);
//       });
//     }

//     function buildSub(cat) {
//       if (!subRow) return;
//       var subs = cat ? subcatsMap[cat] : null;
//       subRow.innerHTML = '';
//       S.sub = 'All';
//       if (!subs || !subs.length) { subRow.classList.remove('show'); return; }
//       subRow.classList.add('show');
//       var ab = document.createElement('button');
//       ab.type = 'button';
//       ab.className = 'bl-sub-btn active';
//       ab.textContent = 'All ' + cat;
//       ab.setAttribute('aria-pressed', 'true');
//       ab.onclick = function () {
//         S.sub = 'All';
//         subRow.querySelectorAll('.bl-sub-btn').forEach(function (x) {
//           var active = x === ab;
//           x.classList.toggle('active', active);
//           x.setAttribute('aria-pressed', active ? 'true' : 'false');
//         });
//         render();
//       };
//       subRow.appendChild(ab);
//       subs.forEach(function (s) {
//         var b = document.createElement('button');
//         b.type = 'button';
//         b.className = 'bl-sub-btn';
//         b.textContent = s;
//         b.setAttribute('aria-pressed', 'false');
//         b.onclick = function () {
//           S.sub = s;
//           subRow.querySelectorAll('.bl-sub-btn').forEach(function (x) {
//             var active = x === b;
//             x.classList.toggle('active', active);
//             x.setAttribute('aria-pressed', active ? 'true' : 'false');
//           });
//           render();
//         };
//         subRow.appendChild(b);
//       });
//     }

//     function buildAZ() {
//       if (!azRow) return;
//       var lbl = document.createElement('span');
//       lbl.className = 'bl-az-label';
//       lbl.textContent = 'A–Z';
//       azRow.appendChild(lbl);

//       var used = {};
//       BRANDS.forEach(function (b) { used[b.letter] = true; });

//       ALPHABET.forEach(function (l) {
//         var b = document.createElement('button');
//         b.type = 'button';
//         b.className = 'bl-az-btn ' + (used[l] ? 'has' : 'empty');
//         b.setAttribute('aria-pressed', 'false');
//         if (!used[l]) b.setAttribute('aria-disabled', 'true');
//         b.textContent = l;
//         b.dataset.l = l;
//         b.onclick = function () {
//           if (!used[l]) return;
//           S.letter = (S.letter === l) ? null : l;
//           azRow.querySelectorAll('.bl-az-btn').forEach(function (x) {
//             var active = x.dataset.l === S.letter;
//             x.classList.toggle('active', active);
//             x.setAttribute('aria-pressed', active ? 'true' : 'false');
//           });
//           render();
//         };
//         azRow.appendChild(b);
//       });
//     }

//     function makeCard(b) {
//       var ini = b.letter || 'G';
//       var a = document.createElement('a');
//       a.className = 'bl-card';
//       a.href = b.url || '#';
//       a.rel = 'noopener';

//       var imgWrap = document.createElement('div');
//       imgWrap.className = 'bl-card-img';
//       if (b.image) {
//         var img = new Image();
//         img.alt = b.name;
//         img.loading = 'lazy';
//         img.onerror = function () {
//           imgWrap.innerHTML = '<div class="bl-img-fallback"><span class="bl-img-fallback-letter">' + ini + '</span></div>';
//         };
//         img.src = b.image;
//         imgWrap.appendChild(img);
//       } else {
//         imgWrap.innerHTML = '<div class="bl-img-fallback"><span class="bl-img-fallback-letter">' + ini + '</span></div>';
//       }
//       a.appendChild(imgWrap);

//       a.insertAdjacentHTML('beforeend', '<div class="bl-card-overlay"></div>');

//       var badgesHtml = '';
//       if (b.featured && b.featuredBadge) badgesHtml += '<div class="bl-badge">' + escapeHtml(b.featuredBadge) + '</div>';
//       if (b.sale && b.saleBadge) badgesHtml += '<div class="bl-badge-sale">' + escapeHtml(b.saleBadge) + '</div>';
//       if (badgesHtml) a.insertAdjacentHTML('beforeend', '<div class="bl-card-badges">' + badgesHtml + '</div>');

//       a.insertAdjacentHTML('beforeend',
//         '<div class="bl-card-arrow">' +
//         '<svg width="11" height="11" fill="none" stroke="#fff" stroke-width="1.8" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7"/></svg>' +
//         '</div>'
//       );

//       var info = document.createElement('div');
//       info.className = 'bl-card-info';
//       var catsHtml = (b.categories || []).map(function (c) { return '<span class="bl-ctag">' + escapeHtml(c) + '</span>'; }).join('');
//       info.innerHTML = '<div class="bl-card-name">' + escapeHtml(b.name) + '</div><div class="bl-card-cats">' + catsHtml + '</div>';
//       a.appendChild(info);

//       return a;
//     }

//     function render() {
//       var list = filtered();
//       if (!contentEl) return;

//       if (!list.length) {
//         if (emptyEl) emptyEl.classList.add('show');
//         contentEl.innerHTML = '';
//         updateCount(0);
//         return;
//       }
//       if (emptyEl) emptyEl.classList.remove('show');
//       contentEl.innerHTML = '';

//       var g = {};
//       list.forEach(function (b) {
//         if (!g[b.letter]) g[b.letter] = [];
//         g[b.letter].push(b);
//       });

//       var wrap = document.createElement('div');
//       wrap.className = 'bl-brands-wrap';
//       contentEl.appendChild(wrap);

//       Object.keys(g).sort().forEach(function (letter) {
//         var sec = document.createElement('div');
//         sec.className = 'bl-letter-section';

//         var lbl = document.createElement('div');
//         lbl.className = 'bl-letter-label';
//         lbl.innerHTML = '<span class="bl-label-char">' + letter + '</span>';
//         sec.appendChild(lbl);

//         var cards = document.createElement('div');
//         cards.className = 'bl-section-cards';
//         g[letter].forEach(function (b) { cards.appendChild(makeCard(b)); });
//         sec.appendChild(cards);
//         wrap.appendChild(sec);
//       });

//       updateCount(list.length);
//     }

//     function updateCount(n) {
//       if (navCount) navCount.textContent = n + ' brand' + (n !== 1 ? 's' : '');
//     }

//     var resetBtn = scope.querySelector('[data-bl-reset]');
//     if (resetBtn) {
//       resetBtn.onclick = function () {
//         S = { cat: 'All', sub: 'All', letter: null };
//         if (catRow) catRow.querySelectorAll('.bl-cat-btn').forEach(function (b) {
//           var active = b.dataset.cat === 'All';
//           b.classList.toggle('active', active);
//           b.setAttribute('aria-pressed', active ? 'true' : 'false');
//         });
//         if (azRow) azRow.querySelectorAll('.bl-az-btn').forEach(function (b) {
//           b.classList.remove('active');
//           b.setAttribute('aria-pressed', 'false');
//         });
//         if (subRow) { subRow.classList.remove('show'); subRow.innerHTML = ''; }
//         render();
//       };
//     }

//     function escapeHtml(s) {
//       return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
//         return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
//       });
//     }

//     buildCats();
//     buildAZ();
//     render();
//   }

//   /* ========================================================================
//      MOBILE
//      ======================================================================== */
//   function initMobile(root, BRANDS, catList, subcatsMap) {
//     var scope = root.querySelector('.bl-mobile');
//     if (!scope) return;

//     var chipsInner = scope.querySelector('[data-bl-chips]');
//     var sortBtnsRow = scope.querySelector('[data-bl-sort-btns]');
//     var grid = scope.querySelector('[data-bl-grid]');
//     var emptyEl = scope.querySelector('[data-bl-m-empty]');
//     var hdrCount = scope.querySelector('[data-bl-m-count]');
//     var searchInput = scope.querySelector('[data-bl-search-input]');
//     var searchClear = scope.querySelector('[data-bl-search-clear]');
//     var fab = scope.querySelector('[data-bl-fab]');
//     var fabDot = scope.querySelector('[data-bl-fab-dot]');
//     var backdrop = scope.querySelector('[data-bl-backdrop]');
//     var drawer = scope.querySelector('[data-bl-drawer]');
//     var drawerClose = scope.querySelector('[data-bl-drawer-close]');
//     var dCats = scope.querySelector('[data-bl-d-cats]');
//     var dSort = scope.querySelector('[data-bl-d-sort]');
//     var dAZ = scope.querySelector('[data-bl-d-az]');
//     var dReset = scope.querySelector('[data-bl-d-reset]');
//     var dApply = scope.querySelector('[data-bl-d-apply]');

//     var S = { cat: 'All', search: '', sort: 'default', letter: '' };

//     function filtered() {
//       var list = BRANDS.filter(function (b) {
//         var cm = S.cat === 'All' || (S.cat === 'Sale' ? b.sale === true : (b.categories || []).indexOf(S.cat) !== -1);
//         var lm = !S.letter || b.letter === S.letter;
//         var qm = !S.search || b.name.toLowerCase().indexOf(S.search.toLowerCase()) !== -1;
//         return cm && lm && qm;
//       });
//       if (S.sort === 'az') list = list.slice().sort(function (a, b) { return a.name.localeCompare(b.name); });
//       else if (S.sort === 'za') list = list.slice().sort(function (a, b) { return b.name.localeCompare(a.name); });
//       else list = list.slice().sort(function (a, b) { return (b.featured === true) - (a.featured === true); });
//       return list;
//     }

//     function escapeHtml(s) {
//       return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
//         return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
//       });
//     }

//     function makeCard(b) {
//       var ini = b.letter || 'G';
//       var card = document.createElement('a');
//       card.className = 'bl-m-gcard';
//       card.href = b.url || '#';
//       card.rel = 'noopener';

//       var imgWrap = document.createElement('div');
//       imgWrap.className = 'bl-m-gcard-img';
//       var badgesHtml = '';
//       if (b.featured && b.featuredBadge) badgesHtml += '<div class="bl-m-gcard-badge">' + escapeHtml(b.featuredBadge) + '</div>';
//       if (b.sale && b.saleBadge) badgesHtml += '<div class="bl-m-gcard-badge sale">' + escapeHtml(b.saleBadge) + '</div>';
//       if (badgesHtml) imgWrap.insertAdjacentHTML('beforeend', '<div class="bl-m-gcard-badges">' + badgesHtml + '</div>');

//       if (b.image) {
//         var img = new Image();
//         img.alt = b.name;
//         img.loading = 'lazy';
//         img.onerror = function () { imgWrap.innerHTML = '<div class="bl-m-gcard-img-fallback">' + ini + '</div>'; };
//         img.src = b.image;
//         imgWrap.appendChild(img);
//       } else {
//         imgWrap.innerHTML = '<div class="bl-m-gcard-img-fallback">' + ini + '</div>';
//       }
//       card.appendChild(imgWrap);

//       var body = document.createElement('div');
//       body.className = 'bl-m-gcard-body';
//       var catsHtml = (b.categories || []).map(function (c) { return '<span class="bl-m-gcard-tag">' + escapeHtml(c) + '</span>'; }).join('');
//       body.innerHTML = '<div class="bl-m-gcard-name">' + escapeHtml(b.name) + '</div>' +
//         '<div class="bl-m-gcard-cats">' + catsHtml + '</div>' +
//         '<div class="bl-m-gcard-cta">Shop Collection <svg width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7"/></svg></div>';
//       card.appendChild(body);
//       return card;
//     }

//     function render() {
//       if (!grid) return;
//       var list = filtered();
//       grid.innerHTML = '';
//       if (!list.length) {
//         if (emptyEl) emptyEl.classList.add('show');
//       } else {
//         if (emptyEl) emptyEl.classList.remove('show');
//         list.forEach(function (b) { grid.appendChild(makeCard(b)); });
//       }
//       if (hdrCount) hdrCount.textContent = list.length + ' brand' + (list.length !== 1 ? 's' : '');
//     }

//     function updateFabDot() {
//       if (!fabDot) return;
//       var count = (S.cat !== 'All' ? 1 : 0) + (S.letter ? 1 : 0);
//       if (count > 0) {
//         fabDot.textContent = String(count);
//         fabDot.classList.add('on');
//       } else {
//         fabDot.textContent = '';
//         fabDot.classList.remove('on');
//       }
//     }

//     function setFilter(key) {
//       S.cat = key;
//       chipsInner && chipsInner.querySelectorAll('.bl-m-chip').forEach(function (x) {
//         x.classList.toggle('active', x.dataset.key === key);
//       });
//       dCats && dCats.querySelectorAll('.bl-m-d-cat').forEach(function (x) {
//         x.classList.toggle('active', x.dataset.key === key);
//       });
//       updateFabDot();
//       render();
//     }

//     function openDrawer() {
//       if (backdrop) backdrop.classList.add('open');
//       if (drawer) drawer.classList.add('open');
//       document.body.style.overflow = 'hidden';
//       if (drawer) {
//         var focusable = drawer.querySelector('button, [href], input');
//         if (focusable) focusable.focus();
//       }
//     }
//     function closeDrawer() {
//       if (backdrop) backdrop.classList.remove('open');
//       if (drawer) drawer.classList.remove('open');
//       document.body.style.overflow = '';
//     }

//     if (fab) fab.onclick = openDrawer;
//     if (drawerClose) drawerClose.onclick = closeDrawer;
//     if (backdrop) backdrop.onclick = closeDrawer;
//     if (dApply) dApply.onclick = closeDrawer;

//     if (searchInput) {
//       searchInput.addEventListener('input', function () {
//         S.search = searchInput.value;
//         if (searchClear) searchClear.classList.toggle('show', !!S.search);
//         render();
//       });
//     }
//     if (searchClear) {
//       searchClear.onclick = function () {
//         S.search = '';
//         if (searchInput) searchInput.value = '';
//         searchClear.classList.remove('show');
//         render();
//       };
//     }

//     function buildChips() {
//       if (!chipsInner) return;
//       catList.forEach(function (c) {
//         var key = c.key;
//         var el = document.createElement('div');
//         el.className = 'bl-m-chip' + (key === 'All' ? ' active' : '') + (key === 'Sale' ? ' sale' : '');
//         el.setAttribute('role', 'button');
//         el.setAttribute('tabindex', '0');
//         el.dataset.key = key;
//         el.textContent = c.label;
//         el.onclick = function () { setFilter(key); };
//         el.onkeydown = function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFilter(key); } };
//         chipsInner.appendChild(el);
//       });
//     }

//     function buildSortBtns() {
//       if (!sortBtnsRow) return;
//       [['Featured', 'default'], ['A-Z', 'az'], ['Z-A', 'za']].forEach(function (item) {
//         var label = item[0], key = item[1];
//         var btn = document.createElement('button');
//         btn.type = 'button';
//         btn.className = 'bl-m-sort-btn' + (key === 'default' ? ' active' : '');
//         btn.textContent = label;
//         btn.dataset.key = key;
//         btn.onclick = function () {
//           S.sort = key;
//           sortBtnsRow.querySelectorAll('.bl-m-sort-btn').forEach(function (b) { b.classList.toggle('active', b.dataset.key === key); });
//           dSort && dSort.querySelectorAll('.bl-m-d-sort-pill').forEach(function (b) { b.classList.toggle('active', b.dataset.key === key); });
//           render();
//         };
//         sortBtnsRow.appendChild(btn);
//       });
//     }

//     function buildDrawer() {
//       if (dCats) {
//         catList.forEach(function (c) {
//           var label = c.label, key = c.key;
//           var el = document.createElement('div');
//           el.className = 'bl-m-d-cat' + (S.cat === key ? ' active' : '') + (key === 'Sale' ? ' sale-row' : '');
//           el.setAttribute('role', 'button');
//           el.setAttribute('tabindex', '0');
//           el.dataset.key = key;
//           el.innerHTML = escapeHtml(label) + '<div class="bl-m-d-check"></div>';
//           el.onclick = function () { setFilter(key); };
//           el.onkeydown = function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFilter(key); } };
//           dCats.appendChild(el);
//         });
//       }

//       if (dSort) {
//         [['Featured', 'default'], ['A-Z', 'az'], ['Z-A', 'za']].forEach(function (item) {
//           var label = item[0], key = item[1];
//           var el = document.createElement('div');
//           el.className = 'bl-m-d-sort-pill' + (S.sort === key ? ' active' : '');
//           el.setAttribute('role', 'button');
//           el.setAttribute('tabindex', '0');
//           el.dataset.key = key;
//           el.textContent = label;
//           el.onclick = function () {
//             S.sort = key;
//             dSort.querySelectorAll('.bl-m-d-sort-pill').forEach(function (x) { x.classList.toggle('active', x === el); });
//             sortBtnsRow && sortBtnsRow.querySelectorAll('.bl-m-sort-btn').forEach(function (b) { b.classList.toggle('active', b.dataset.key === key); });
//             render();
//           };
//           el.onkeydown = function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); el.onclick(); } };
//           dSort.appendChild(el);
//         });
//       }

//       if (dAZ) {
//         var used = {};
//         BRANDS.forEach(function (b) { used[b.letter] = true; });
//         ALPHABET.forEach(function (l) {
//           var el = document.createElement('div');
//           el.className = 'bl-m-az ' + (used[l] ? 'has' : 'empty') + (S.letter === l ? ' active' : '');
//           el.setAttribute('role', 'button');
//           el.setAttribute('tabindex', used[l] ? '0' : '-1');
//           el.textContent = l;
//           el.onclick = function () {
//             if (!used[l]) return;
//             S.letter = (S.letter === l) ? '' : l;
//             dAZ.querySelectorAll('.bl-m-az').forEach(function (x) { x.classList.toggle('active', x.textContent === S.letter); });
//             updateFabDot();
//             render();
//           };
//           el.onkeydown = function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); el.onclick(); } };
//           dAZ.appendChild(el);
//         });
//       }
//     }

//     if (dReset) {
//       dReset.onclick = function () {
//         S = { cat: 'All', search: '', sort: 'default', letter: '' };
//         if (searchInput) searchInput.value = '';
//         if (searchClear) searchClear.classList.remove('show');
//         chipsInner && chipsInner.querySelectorAll('.bl-m-chip').forEach(function (x) { x.classList.toggle('active', x.dataset.key === 'All'); });
//         sortBtnsRow && sortBtnsRow.querySelectorAll('.bl-m-sort-btn').forEach(function (x) { x.classList.toggle('active', x.dataset.key === 'default'); });
//         dCats && dCats.querySelectorAll('.bl-m-d-cat').forEach(function (x) { x.classList.toggle('active', x.dataset.key === 'All'); });
//         dSort && dSort.querySelectorAll('.bl-m-d-sort-pill').forEach(function (x) { x.classList.toggle('active', x.dataset.key === 'default'); });
//         dAZ && dAZ.querySelectorAll('.bl-m-az').forEach(function (x) { x.classList.remove('active'); });
//         updateFabDot();
//         render();
//       };
//     }

//     buildChips();
//     buildSortBtns();
//     buildDrawer();
//     render();
//   }

//   function boot() {
//     document.querySelectorAll('[data-brand-lounge]').forEach(initBrandLounge);
//   }

//   if (document.readyState === 'loading') {
//     document.addEventListener('DOMContentLoaded', boot);
//   } else {
//     boot();
//   }

//   document.addEventListener('shopify:section:load', function (e) {
//     var root = e.target.querySelector && e.target.querySelector('[data-brand-lounge]');
//     if (root) initBrandLounge(root);
//   });
// })();